import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Mail, Lock, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'login' | 'register' | 'reset'>('login');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      onClose();
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Login error:', err);

      if (err.message === 'Invalid login credentials') {
        setError(t.login.errors.invalidCredentials);
      } else if (err.message === 'Email not confirmed') {
        setError(t.login.errors.emailNotConfirmed);
      } else if (err.message === 'Invalid email') {
        setError(t.login.errors.invalidEmailFormat);
      } else {
        setError(err.message || t.login.errors.genericLoginError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(email, password, name);
      onClose();
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Registration error:', err);

      if (err.message && err.message.startsWith('PENDING_APPROVAL:')) {
        const message = err.message.replace('PENDING_APPROVAL:', '');
        alert(message);
        setMode('login');
        setEmail('');
        setPassword('');
        setName('');
      } else {
        setError(err.message || t.login.errors.registrationError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email);
      if (resetError) throw resetError;

      alert(t.login.resetEmailSent);
      setMode('login');
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.message || t.login.errors.resetPasswordError);
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-all"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-md mx-4 animate-in">
        <div className="sm:mx-auto sm:w-full sm:max-w-md mb-6">
          <div className="flex justify-center">
            <Heart className="h-12 w-12 text-rose-500" />
          </div>
          <h2 className="mt-4 text-center text-3xl font-bold tracking-tight text-white">
            {t.login.brand}
          </h2>
        </div>

        <div className="bg-white py-8 px-4 shadow-2xl rounded-xl sm:px-10 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          {mode === 'login' && (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="modal-email" className="block text-sm font-medium text-gray-700">
                  {t.login.email}
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="modal-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="modal-password" className="block text-sm font-medium text-gray-700">
                  {t.login.password}
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="modal-password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full justify-center rounded-md border border-transparent bg-rose-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t.login.loggingIn : t.login.logIn}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => { setMode('register'); setError(''); }}
                  className="text-sm font-medium text-rose-600 hover:text-rose-500"
                >
                  {t.login.register}
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('reset'); setError(''); }}
                  className="text-sm font-medium text-rose-600 hover:text-rose-500"
                >
                  {t.login.forgotPassword}
                </button>
              </div>
            </form>
          )}

          {mode === 'register' && (
            <form className="space-y-6" onSubmit={handleRegister}>
              <div>
                <h3 className="text-lg font-medium text-gray-900">{t.login.registrationTitle}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t.login.registrationDescription}
                </p>
              </div>

              <div>
                <label htmlFor="modal-register-name" className="block text-sm font-medium text-gray-700">
                  {t.login.fullName}
                </label>
                <div className="mt-1">
                  <input
                    id="modal-register-name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="modal-register-email" className="block text-sm font-medium text-gray-700">
                  {t.login.email}
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="modal-register-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="modal-register-password" className="block text-sm font-medium text-gray-700">
                  {t.login.password}
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="modal-register-password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full justify-center rounded-md border border-transparent bg-rose-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t.login.registering : t.login.register}
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(''); }}
                  className="text-sm font-medium text-rose-600 hover:text-rose-500"
                >
                  {t.login.alreadyHaveAccount}
                </button>
              </div>
            </form>
          )}

          {mode === 'reset' && (
            <form className="space-y-6" onSubmit={handlePasswordReset}>
              <div>
                <h3 className="text-lg font-medium text-gray-900">{t.login.resetPasswordTitle}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t.login.resetPasswordDescription}
                </p>
              </div>

              <div>
                <label htmlFor="modal-reset-email" className="block text-sm font-medium text-gray-700">
                  {t.login.email}
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="modal-reset-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full justify-center rounded-md border border-transparent bg-rose-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t.login.sending : t.login.sendInstructions}
                </button>
              </div>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(''); }}
                  className="text-sm font-medium text-rose-600 hover:text-rose-500"
                >
                  {t.login.backToLogin}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
